import type { AgendaCategory, Routine } from '@sistemas/contracts'

import { describeDbError, getSupabase } from '@/lib/supabase'

/**
 * Escritas de C5 — catálogo de rotinas e taxonomia da agenda.
 *
 * Mesma regra do resto do Hub: grava no banco primeiro, reflete depois. O
 * estado local é cache; se o RLS recusar, a tela precisa saber antes de mostrar
 * a rotina como criada.
 *
 * Participantes vivem em `routine_participant`, uma tabela à parte, então
 * salvar uma rotina são duas escritas. A segunda pode falhar sozinha (RLS da
 * tabela de participantes é checado contra o dono da rotina) — e quando falha,
 * o retorno diz exatamente isso, em vez de fingir sucesso.
 */

export type RoutineWriteResult<T> = { ok: true; data: T } | { ok: false; reason: string }

interface RoutineRow {
  id: string
  name: string
  kind: Routine['kind']
  category_id: string | null
  weekday: number | null
  month_day: number | null
  week_of_month: number | null
  time_of_day: string
  duration_minutes: number
  owner_id: string
  non_business_day_policy: Routine['nonBusinessDayPolicy']
  is_active: boolean
}

interface CategoryRow {
  id: string
  name: string
  color: AgendaCategory['color']
  description: string | null
  position: number
  is_active: boolean
}

function dbError(error: { message?: string } | null, fallback: string): string {
  return describeDbError(error, fallback)
}

function toRoutine(row: RoutineRow, participantIds: readonly string[]): Routine {
  return {
    id: row.id,
    name: row.name,
    kind: row.kind,
    time: row.time_of_day.slice(0, 5),
    durationMinutes: row.duration_minutes,
    ownerId: row.owner_id,
    nonBusinessDayPolicy: row.non_business_day_policy,
    isActive: row.is_active,
    participantIds: [...participantIds],
    ...(row.category_id ? { categoryId: row.category_id } : {}),
    ...(row.weekday !== null ? { weekday: row.weekday } : {}),
    ...(row.week_of_month !== null ? { weekOfMonth: row.week_of_month } : {}),
    ...(row.month_day !== null ? { monthDay: row.month_day } : {}),
  }
}

function toCategory(row: CategoryRow): AgendaCategory {
  return {
    id: row.id,
    name: row.name,
    color: row.color,
    position: row.position,
    isActive: row.is_active,
    ...(row.description ? { description: row.description } : {}),
  }
}

export interface RoutineInput {
  name: string
  kind: Routine['kind']
  categoryId: string | null
  weekday: number | null
  monthDay: number | null
  weekOfMonth: number | null
  time: string
  durationMinutes: number
  ownerId: string
  nonBusinessDayPolicy: Routine['nonBusinessDayPolicy']
  participantIds: readonly string[]
}

function payloadOf(input: RoutineInput): Record<string, unknown> {
  return {
    name: input.name.trim(),
    kind: input.kind,
    category_id: input.categoryId,
    // A constraint `routine_one_cadence` exige exatamente uma. Zerar a outra
    // aqui é o que deixa trocar de semanal para mensal sem violar o banco.
    weekday: input.weekday,
    month_day: input.monthDay,
    week_of_month: input.weekOfMonth,
    time_of_day: input.time,
    duration_minutes: input.durationMinutes,
    owner_id: input.ownerId,
    non_business_day_policy: input.nonBusinessDayPolicy,
  }
}

// --- Rotina ------------------------------------------------------------------

export async function insertRoutine(
  input: RoutineInput,
): Promise<RoutineWriteResult<Routine>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('routine')
    .insert({ ...payloadOf(input), is_active: true })
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível criar a rotina.') }
  }

  const row = data as RoutineRow
  const participants = await replaceRoutineParticipants(row.id, input.participantIds)
  if (!participants.ok) {
    return {
      ok: false,
      reason: `Rotina criada, mas os participantes não foram salvos: ${participants.reason}`,
    }
  }
  return { ok: true, data: toRoutine(row, participants.data) }
}

export async function patchRoutine(
  id: string,
  input: RoutineInput,
): Promise<RoutineWriteResult<Routine>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('routine')
    .update(payloadOf(input))
    .eq('id', id)
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível salvar a rotina.') }
  }

  const participants = await replaceRoutineParticipants(id, input.participantIds)
  if (!participants.ok) {
    return {
      ok: false,
      reason: `Rotina salva, mas os participantes não foram atualizados: ${participants.reason}`,
    }
  }
  return { ok: true, data: toRoutine(data as RoutineRow, participants.data) }
}

/**
 * Liga/desliga a rotina.
 *
 * Para rotina com histórico, desativar é a operação certa: ela sai da agenda e
 * do catálogo ativo, e as ocorrências realizadas continuam apontando para ela
 * (SDD 09 R3). A migration 019 passou a FK de `occurrence` para `restrict`, de
 * modo que o banco recusa o DELETE nesse caso — a regra não depende da tela.
 */
export async function setRoutineActiveRemote(
  id: string,
  isActive: boolean,
): Promise<RoutineWriteResult<Routine>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('routine')
    .update({ is_active: isActive })
    .eq('id', id)
    .select('*')
    .single()

  if (error || !data) {
    const fallback = isActive
      ? 'Não foi possível reativar a rotina.'
      : 'Não foi possível desativar a rotina.'
    return { ok: false, reason: dbError(error, fallback) }
  }

  const { data: links, error: linksError } = await sb
    .from('routine_participant')
    .select('member_id')
    .eq('routine_id', id)
  if (linksError) {
    return { ok: false, reason: dbError(linksError, 'Falha ao ler os participantes.') }
  }

  const participantIds = ((links ?? []) as { member_id: string }[]).map((r) => r.member_id)
  return { ok: true, data: toRoutine(data as RoutineRow, participantIds) }
}

/**
 * Apaga a rotina de vez.
 *
 * Só funciona para rotina que nunca ocorreu — a criada por engano, a de teste,
 * a semeada que não faz sentido para este departamento. Com histórico, a FK
 * `restrict` recusa (migration 019) e o motivo volta traduzido, com o caminho
 * certo: desativar.
 *
 * `routine_participant` cai por cascade de propósito: vínculo de participação
 * é configuração da rotina que está saindo, não registro do que aconteceu.
 */
export async function deleteRoutineRemote(id: string): Promise<RoutineWriteResult<true>> {
  const sb = getSupabase()
  const { error } = await sb.from('routine').delete().eq('id', id)

  if (error) {
    // 23503 = foreign_key_violation. Só pode ser a ocorrência: é a única FK
    // que aponta para `routine` sem cascade.
    if ((error as { code?: string }).code === '23503') {
      return {
        ok: false,
        reason:
          'Esta rotina já tem ocorrências registradas e o histórico não pode ser apagado. Desative a rotina: ela sai da agenda e as atas continuam no lugar.',
      }
    }
    return { ok: false, reason: dbError(error, 'Não foi possível excluir a rotina.') }
  }
  return { ok: true, data: true }
}

/** Diferencial, não delete-all-insert: evita apagar tudo e recriar igual. */
export async function replaceRoutineParticipants(
  routineId: string,
  memberIds: readonly string[],
): Promise<RoutineWriteResult<string[]>> {
  const sb = getSupabase()
  const next = [...new Set(memberIds)]

  const { data: existing, error: readError } = await sb
    .from('routine_participant')
    .select('member_id')
    .eq('routine_id', routineId)

  if (readError) {
    return { ok: false, reason: dbError(readError, 'Falha ao ler os participantes atuais.') }
  }

  const current = ((existing ?? []) as { member_id: string }[]).map((r) => r.member_id)
  const currentSet = new Set(current)
  const nextSet = new Set(next)
  const toRemove = current.filter((id) => !nextSet.has(id))
  const toAdd = next.filter((id) => !currentSet.has(id))

  if (toRemove.length > 0) {
    const { error } = await sb
      .from('routine_participant')
      .delete()
      .eq('routine_id', routineId)
      .in('member_id', toRemove)
    if (error) {
      return { ok: false, reason: dbError(error, 'Falha ao remover participantes.') }
    }
  }

  if (toAdd.length > 0) {
    const { error } = await sb
      .from('routine_participant')
      .insert(toAdd.map((memberId) => ({ routine_id: routineId, member_id: memberId })))
    if (error) {
      return { ok: false, reason: dbError(error, 'Falha ao incluir participantes.') }
    }
  }

  return { ok: true, data: next }
}

// --- Categoria ---------------------------------------------------------------

export interface CategoryInput {
  name: string
  color: AgendaCategory['color']
  description: string | null
  position: number
}

export async function insertCategory(
  input: CategoryInput,
): Promise<RoutineWriteResult<AgendaCategory>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('agenda_category')
    .insert({
      name: input.name.trim(),
      color: input.color,
      description: input.description?.trim() || null,
      position: input.position,
      is_active: true,
    })
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível criar a categoria.') }
  }
  return { ok: true, data: toCategory(data as CategoryRow) }
}

export async function patchCategory(
  id: string,
  patch: Partial<CategoryInput> & { isActive?: boolean },
): Promise<RoutineWriteResult<AgendaCategory>> {
  const sb = getSupabase()
  const payload: Record<string, unknown> = {}
  if (patch.name !== undefined) payload.name = patch.name.trim()
  if (patch.color !== undefined) payload.color = patch.color
  if (patch.description !== undefined) payload.description = patch.description?.trim() || null
  if (patch.position !== undefined) payload.position = patch.position
  if (patch.isActive !== undefined) payload.is_active = patch.isActive

  const { data, error } = await sb
    .from('agenda_category')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível salvar a categoria.') }
  }
  return { ok: true, data: toCategory(data as CategoryRow) }
}
